import { formatInterval, formatScheduleTime, formatSeconds, scheduleTypeMeta } from './mediaflowMeta'

/**
 * Form model, API mapping, validation and overlap maths for the Automation
 * (ad schedule) editor. Pure helpers only — no API calls, no store access.
 *
 * The API payload shapes produced here are byte-for-byte what the previous
 * editor sent; keep `toApiPayload` and `fromApiSchedule` in lockstep with the
 * server contract rather than "tidying" units or key names.
 */

/** Types the picker offers. The others can still be edited when they exist. */
export const CREATABLE_SCHEDULE_TYPES = ['single-time', 'recurring']

export const INTERVAL_PRESETS = [
	'Every 5 minutes',
	'Every 10 minutes',
	'Every 15 minutes',
	'Every 30 minutes',
	'Every 1 hour',
	'Every 2 hours',
]
export const CUSTOM_INTERVAL = 'Custom interval'
export const INTERVAL_OPTIONS = [...INTERVAL_PRESETS, CUSTOM_INTERVAL]
export const CUSTOM_UNITS = ['Seconds', 'Minutes']

let rowSeq = 0
const rowId = () => `row-${Date.now()}-${(rowSeq += 1)}`

export const emptyAd = () => ({ id: rowId(), flowId: null, duration: '30' })
export const emptyScene = () => ({ id: rowId(), name: '' })

/**
 * One flat form shared by every type. Each type only reads the fields it
 * needs, so switching type keeps what the operator already filled in.
 */
export const emptyForm = () => ({
	name: '',
	type: null,
	flowId: null,
	startTime: null,
	endTime: null,
	duration: '30',
	returnFlowId: null,
	interval: null,
	customValue: '',
	customUnit: 'Seconds',
	ads: [emptyAd()],
	scenes: [emptyScene()],
})

// ---------------------------------------------------------------- time helpers

const pad2 = (n) => String(n).padStart(2, '0')

/** Server time (UTC ISO without zone, bare "HH:mm:ss", or {hours,minutes}) → local Date. */
export const utcToLocalDate = (value) => {
	if (!value) return null
	if (value instanceof Date) return value

	let h; let m; let s
	if (typeof value === 'object' && value.hours !== undefined) {
		h = value.hours; m = value.minutes ?? 0; s = value.seconds ?? 0
	} else if (typeof value === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(value)) {
		[h, m, s] = value.split(':').map(Number)
	} else if (typeof value === 'string') {
		const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/)
		if (match) {
			const [, year, month, day, hours, minutes, seconds] = match.map(Number)
			return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds || 0))
		}
		const parsed = new Date(value)
		return Number.isNaN(parsed.getTime()) ? null : parsed
	} else {
		return null
	}

	// Clock-only values: today, converted from UTC to the viewer's zone.
	const today = new Date()
	const utc = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), h, m, s))
	return new Date(today.getFullYear(), today.getMonth(), today.getDate(), utc.getHours(), utc.getMinutes(), utc.getSeconds())
}

/** Local Date (or clock value) → "YYYY-MM-DDTHH:mm:ss" in UTC, as the server expects. */
export const localToUtcString = (value) => {
	if (!value) return null
	let date
	if (typeof value === 'object' && value !== null && !(value instanceof Date) && value.hours !== undefined) {
		const today = new Date()
		date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), value.hours, value.minutes ?? 0, value.seconds ?? 0)
	} else if (value instanceof Date) {
		date = value
	} else if (typeof value === 'string') {
		if (/^\d{2}:\d{2}:\d{2}$/.test(value)) {
			const [h, m, s] = value.split(':').map(Number)
			const today = new Date()
			date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), h, m, s)
		} else {
			date = new Date(value)
		}
	} else {
		return null
	}
	if (Number.isNaN(date.getTime())) return null
	return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}T${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}:${pad2(date.getUTCSeconds())}`
}

/** Seconds since local midnight for a Date or {hours,minutes,seconds}. */
const secondsOfDay = (value) => {
	if (!value) return null
	if (value instanceof Date) return value.getHours() * 3600 + value.getMinutes() * 60 + value.getSeconds()
	if (typeof value === 'object' && value.hours !== undefined) return (value.hours || 0) * 3600 + (value.minutes || 0) * 60 + (value.seconds || 0)
	return null
}

// ---------------------------------------------------------------- intervals

/** "Every 15 minutes" / "Custom interval" + custom fields → seconds, or null. */
export const intervalToSeconds = (label, customValue, customUnit) => {
	if (label === CUSTOM_INTERVAL) {
		const value = parseInt(customValue, 10) || 0
		const unit = String(customUnit || 'seconds').toLowerCase()
		if (unit === 'minutes') return value * 60
		if (unit === 'hours') return value * 3600
		return value
	}
	const match = label?.match(/Every (\d+) (minute|hour|second)/i)
	if (!match) return null
	const value = parseInt(match[1], 10)
	const unit = match[2].toLowerCase()
	if (unit === 'minute') return value * 60
	if (unit === 'hour') return value * 3600
	return value
}

/** API `{ value, unit }` → seconds. */
const apiIntervalToSeconds = (interval) => {
	if (!interval || typeof interval !== 'object') return null
	const value = interval.value || 0
	const unit = String(interval.unit || 'seconds').toLowerCase()
	if (unit === 'minutes' || unit === 'minute') return value * 60
	if (unit === 'hours' || unit === 'hour') return value * 3600
	return value
}

/**
 * API interval → preset label when it matches one, otherwise "Custom interval".
 * Mirrors the previous editor: units arrive singular or plural.
 */
export const apiIntervalToLabel = (interval) => {
	if (!interval) return null
	if (typeof interval === 'string') return interval
	if (typeof interval === 'object' && interval.value !== undefined && interval.unit !== undefined) {
		const value = interval.value
		const unit = String(interval.unit).toLowerCase()
		let word = unit
		if (unit === 'second' || unit === 'seconds') word = value === 1 ? 'second' : 'seconds'
		else if (unit === 'minute' || unit === 'minutes') word = value === 1 ? 'minute' : 'minutes'
		else if (unit === 'hour' || unit === 'hours') word = value === 1 ? 'hour' : 'hours'
		const label = `Every ${value} ${word}`
		return INTERVAL_OPTIONS.includes(label) ? label : CUSTOM_INTERVAL
	}
	return null
}

/** API custom interval unit → dropdown unit ("Minutes" / "Seconds"). */
const apiUnitToCustomUnit = (interval) => {
	const unit = String(interval?.unit || '').toLowerCase()
	return unit === 'minute' || unit === 'minutes' ? 'Minutes' : 'Seconds'
}

/**
 * Form interval → API `{ value, unit }`. Presets keep the historic unit
 * spelling ("minutes", but "hour" / "second"); custom uses the lowercased
 * dropdown unit. Falls back to the raw label for anything unrecognised.
 */
const intervalToApi = (label, customValue, customUnit) => {
	if (label === CUSTOM_INTERVAL) {
		return { value: parseInt(customValue, 10), unit: String(customUnit).toLowerCase() }
	}
	const match = label?.match(/Every (\d+) (minute|hour|second)/i)
	if (!match) return label
	const unit = match[2].toLowerCase()
	return { value: parseInt(match[1], 10), unit: unit + (unit === 'minute' ? 's' : '') }
}

// ---------------------------------------------------------------- API mapping

/** Server schedule → form. */
export const fromApiSchedule = (schedule) => {
	const form = emptyForm()
	form.name = schedule?.name || ''
	form.type = schedule?.type || null
	if (!schedule) return form

	const fillInterval = (interval) => {
		form.interval = apiIntervalToLabel(interval)
		if (form.interval === CUSTOM_INTERVAL && typeof interval === 'object' && interval !== null) {
			form.customValue = interval.value?.toString() || ''
			form.customUnit = apiUnitToCustomUnit(interval)
		} else {
			form.customValue = ''
			form.customUnit = 'Seconds'
		}
	}

	switch (schedule.type) {
		case 'single-time':
			form.flowId = schedule.flowId || null
			form.startTime = utcToLocalDate(schedule.startTime)
			form.duration = schedule.duration?.toString() || '30'
			form.returnFlowId = schedule.returnFlowId || null
			break
		case 'recurring':
			form.flowId = schedule.flowId || null
			form.startTime = utcToLocalDate(schedule.startTime)
			form.endTime = utcToLocalDate(schedule.endTime)
			fillInterval(schedule.repeatInterval)
			form.duration = schedule.duration?.toString() || '30'
			form.returnFlowId = schedule.returnFlowId || null
			break
		case 'multi-ad-block':
			form.startTime = utcToLocalDate(schedule.startTime)
			form.returnFlowId = schedule.returnFlowId || null
			form.ads = schedule.ads?.map((ad) => ({ id: rowId(), flowId: ad.flowId || null, duration: ad.duration?.toString() || '30' })) || [emptyAd()]
			break
		case 'scene-rotation':
			fillInterval(schedule.rotationInterval)
			form.duration = schedule.duration?.toString() || '30'
			form.returnFlowId = schedule.returnFlowId || null
			form.scenes = schedule.scenes?.map((scene) => ({ id: rowId(), name: scene.name || '' })) || [emptyScene()]
			break
		case 'time-slot-block':
			form.returnFlowId = schedule.returnFlowId || null
			form.startTime = utcToLocalDate(schedule.startTime)
			form.endTime = utcToLocalDate(schedule.endTime)
			fillInterval(schedule.playInterval)
			form.duration = schedule.duration?.toString() || '30'
			break
		default:
			break
	}
	return form
}

/** Form → request body for POST / PUT. Key order matches the previous editor. */
export const toApiPayload = (form) => {
	const payload = { name: form.name.trim(), type: form.type }
	switch (form.type) {
		case 'single-time':
			payload.flowId = form.flowId
			payload.startTime = localToUtcString(form.startTime)
			payload.duration = parseInt(form.duration, 10)
			payload.returnFlowId = form.returnFlowId
			break
		case 'recurring':
			payload.flowId = form.flowId
			payload.startTime = localToUtcString(form.startTime)
			payload.endTime = localToUtcString(form.endTime)
			payload.returnFlowId = form.returnFlowId
			payload.duration = parseInt(form.duration, 10)
			payload.repeatInterval = intervalToApi(form.interval, form.customValue, form.customUnit)
			break
		case 'multi-ad-block':
			payload.startTime = localToUtcString(form.startTime)
			payload.returnFlowId = form.returnFlowId
			payload.ads = form.ads.map((ad) => ({ flowId: ad.flowId, duration: parseInt(ad.duration, 10) }))
			break
		case 'scene-rotation':
			payload.returnFlowId = form.returnFlowId
			payload.duration = parseInt(form.duration, 10)
			payload.rotationInterval = intervalToApi(form.interval, form.customValue, form.customUnit)
			payload.scenes = form.scenes.map((scene) => ({ name: scene.name.trim() }))
			break
		case 'time-slot-block':
			payload.returnFlowId = form.returnFlowId
			payload.startTime = localToUtcString(form.startTime)
			payload.endTime = localToUtcString(form.endTime)
			payload.duration = parseInt(form.duration, 10)
			payload.playInterval = intervalToApi(form.interval, form.customValue, form.customUnit)
			break
		default:
			break
	}
	return payload
}

// ---------------------------------------------------------------- validation

const positiveInt = (value) => !!value && parseInt(value, 10) > 0

/**
 * Field → message for everything that would stop a save. Empty object means
 * the form can be submitted. Same rules as the previous editor's disabled
 * state, phrased for inline display.
 */
export const validateForm = (form) => {
	const errors = {}
	if (!form.name.trim()) errors.name = 'Give this schedule a name.'
	if (!form.type) {
		errors.type = 'Pick a schedule type.'
		return errors
	}

	const needsCustom = form.interval === CUSTOM_INTERVAL
	const intervalErrors = () => {
		if (!form.interval) errors.interval = 'Pick how often it repeats.'
		else if (needsCustom && !positiveInt(form.customValue)) errors.customValue = 'Enter a number above 0.'
		else if (needsCustom && !form.customUnit) errors.customValue = 'Pick a unit.'
	}
	const durationError = () => {
		if (!positiveInt(form.duration)) errors.duration = 'Duration must be above 0 seconds.'
	}
	const returnError = () => {
		if (!form.returnFlowId) errors.returnFlowId = 'Pick the scene to return to.'
	}

	switch (form.type) {
		case 'single-time':
			if (!form.flowId) errors.flowId = 'Pick the scene or break to switch to.'
			if (!form.startTime) errors.startTime = 'Pick a date and time.'
			durationError()
			returnError()
			break
		case 'recurring':
			if (!form.flowId) errors.flowId = 'Pick the scene or break to switch to.'
			if (!form.startTime) errors.startTime = 'Pick when it starts.'
			if (!form.endTime) errors.endTime = 'Pick when it ends.'
			intervalErrors()
			durationError()
			returnError()
			break
		case 'multi-ad-block':
			if (!form.startTime) errors.startTime = 'Pick a date and time.'
			returnError()
			if (!form.ads.length) errors.ads = 'Add at least one ad.'
			else if (form.ads.some((ad) => !ad.flowId || !positiveInt(ad.duration))) errors.ads = 'Every ad needs a scene or break and a duration above 0.'
			break
		case 'scene-rotation':
			intervalErrors()
			durationError()
			returnError()
			if (!form.scenes.length || form.scenes.some((scene) => !scene.name.trim())) errors.scenes = 'Every scene needs a name.'
			break
		case 'time-slot-block':
			returnError()
			if (!form.startTime) errors.startTime = 'Pick when the slot starts.'
			if (!form.endTime) errors.endTime = 'Pick when the slot ends.'
			intervalErrors()
			durationError()
			break
		default:
			break
	}
	return errors
}

// ---------------------------------------------------------------- overlaps

const DAY = 86_400

/** Expand a schedule into [start, end) windows in seconds of the local day. */
const expandRanges = ({ type, start, end, interval, duration }) => {
	const ranges = []
	const d = duration || 0
	switch (type) {
		case 'single-time':
		case 'multi-ad-block':
			if (start !== null && start !== undefined) ranges.push({ start, end: start + d })
			break
		case 'recurring':
		case 'time-slot-block':
			if (start !== null && end !== null && interval > 0) {
				for (let t = start; t < end; t += interval) ranges.push({ start: t, end: Math.min(t + d, end) })
			}
			break
		case 'scene-rotation':
			if (interval > 0) {
				for (let t = 0; t < DAY; t += interval) ranges.push({ start: t, end: t + d })
			}
			break
		default:
			break
	}
	return ranges
}

const formRanges = (form) => expandRanges({
	type: form.type,
	start: secondsOfDay(form.startTime),
	end: secondsOfDay(form.endTime),
	interval: intervalToSeconds(form.interval, form.customValue, form.customUnit),
	duration: form.type === 'multi-ad-block'
		? form.ads.reduce((sum, ad) => sum + (parseInt(ad.duration, 10) || 0), 0)
		: parseInt(form.duration, 10) || 0,
})

const apiRanges = (schedule) => {
	const interval = schedule.type === 'recurring' ? schedule.repeatInterval
		: schedule.type === 'scene-rotation' ? schedule.rotationInterval
			: schedule.type === 'time-slot-block' ? schedule.playInterval : null
	return expandRanges({
		type: schedule.type,
		start: secondsOfDay(utcToLocalDate(schedule.startTime)),
		end: secondsOfDay(utcToLocalDate(schedule.endTime)),
		interval: apiIntervalToSeconds(interval),
		duration: schedule.type === 'multi-ad-block'
			? (schedule.ads || []).reduce((sum, ad) => sum + (parseInt(ad.duration, 10) || 0), 0)
			: parseInt(schedule.duration, 10) || 0,
	})
}

/**
 * The first active schedule whose windows collide with the form's, ignoring
 * the one being edited. Null when nothing collides (or nothing to compare).
 */
export const findOverlap = (form, existing = [], editingId = null) => {
	if (!form?.type) return null
	const current = formRanges(form)
	if (!current.length) return null
	for (const schedule of existing) {
		if (!schedule?.enabled || (editingId && schedule._id === editingId)) continue
		const theirs = apiRanges(schedule)
		for (const a of current) {
			for (const b of theirs) {
				if (a.start < b.end && a.end > b.start) return schedule
			}
		}
	}
	return null
}

// ---------------------------------------------------------------- summary

const clock = (value) => (value ? formatScheduleTime(value, { withDate: false }) : null)
const dayAndClock = (value) => (value ? formatScheduleTime(value) : null)
const list = (items) => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`)

/**
 * One sentence that reads the form back to the operator, e.g.
 * "Every 15 min between 09:00–22:00, play Ad break for 45s, then return to Main camera."
 * Missing pieces read as prompts ("a flow", "a time") rather than blanks.
 */
export const summarizeForm = (form, flowName = () => '') => {
	if (!form?.type) return ''
	const flow = (id) => (id && flowName(id)) || 'a scene'
	const back = form.returnFlowId ? `, then return to ${flow(form.returnFlowId)}.` : ', then return to a flow.'
	const dur = positiveInt(form.duration) ? formatSeconds(form.duration) : 'a while'
	const every = form.interval
		? (form.interval === CUSTOM_INTERVAL
			? (positiveInt(form.customValue) ? formatInterval({ value: form.customValue, unit: form.customUnit }) : 'every …')
			: formatInterval(form.interval))
		: 'every …'
	const window = form.startTime || form.endTime
		? `between ${clock(form.startTime) || '…'}–${clock(form.endTime) || '…'}`
		: 'between … and …'
	const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1)

	switch (form.type) {
		case 'single-time':
			return `${form.startTime ? `On ${dayAndClock(form.startTime)}` : 'At a time you pick'}, switch to ${flow(form.flowId)} for ${dur}${back}`
		case 'recurring':
			return `${cap(every)} ${window}, play ${flow(form.flowId)} for ${dur}${back}`
		case 'time-slot-block':
			return `${cap(window)}, run a slot ${every} for ${dur}${back}`
		case 'scene-rotation': {
			const names = form.scenes.map((s) => s.name.trim()).filter(Boolean)
			return `${cap(every)}, rotate through ${names.length ? list(names) : 'your scenes'} for ${dur} each${back}`
		}
		case 'multi-ad-block': {
			const ads = form.ads.filter((ad) => ad.flowId).map((ad) => `${flow(ad.flowId)}${positiveInt(ad.duration) ? ` (${formatSeconds(ad.duration)})` : ''}`)
			return `${form.startTime ? `On ${dayAndClock(form.startTime)}` : 'At a time you pick'}, play ${ads.length ? ads.join(' → ') : 'your ads back to back'}${back}`
		}
		default:
			return scheduleTypeMeta(form.type).description
	}
}
